import { gql } from "@apollo/client";
import { PropsWithChildren, createContext, useContext, useState } from "react";
import { useEditorQueryCode, useEditorQueryId } from "gui/editor/context";
import { useSessionWithUser } from "gui/session/session";
import { apolloCore } from "lib/apollo/apollo";
import {
  InsertWandCompletionDocument,
  InsertWandCompletionMutation,
  InsertWandCompletionMutationVariables,
  UpdateWandCompletionQueryDocument,
  DebugWandQueryDocument,
  DebugWandQueryMutation,
  DebugWandQueryMutationVariables,
  CreateWandQueryMutation,
  CreateWandQueryMutationVariables,
  CreateWandQueryDocument,
  EditWandQueryMutation,
  EditWandQueryMutationVariables,
  EditWandQueryDocument,
} from "lib/types/graphql";
import { Session, SessionWithUser } from "lib/users/types";
import { addToastNotification } from "shared/Toasts/toastNotificationStore";
import { Mode } from "./ModeButton";

type WandCompletion = {
  response_id: string;
  query: string;
};

type WandState = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  mode: Mode;
  setMode: (mode: Mode) => void;
  isRunning: boolean;
  setIsRunning: (isGenerating: boolean) => void;
  question: string;
  setQuestion: (question: string) => void;
  createQuery: () => Promise<string | undefined>;
  editQuery: () => Promise<string | undefined>;
  updateCompletionQueryId: (queryId: number) => Promise<void>;
};

const WandContext = createContext<WandState | undefined>(undefined);

export function WandProvider({ children }: PropsWithChildren<{}>) {
  const queryId = useEditorQueryId();
  const queryCode = useEditorQueryCode();
  const session = useSessionWithUser();

  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<Mode>(queryCode === "" ? "create" : "edit");
  const [isRunning, setIsRunning] = useState(false);
  const [question, setQuestion] = useState("");
  const [completion, setCompletion] = useState<WandCompletion | null>(null);

  if (session === undefined) {
    return (
      <WandContext.Provider value={undefined}>{children}</WandContext.Provider>
    );
  }

  const createQuery = async () => {
    try {
      setIsRunning(true);
      const completion = await createWandQuery(session, question, queryId);

      if (completion === undefined) {
        return undefined;
      }

      setCompletion(completion);
      return completion.query;
    } finally {
      setIsRunning(false);
    }
  };

  const editQuery = async () => {
    try {
      setIsRunning(true);
      const completion = await editWandQuery(session, question, queryCode);

      if (completion === undefined) {
        return undefined;
      }

      return completion.query;
    } finally {
      setIsRunning(false);
    }
  };

  const updateCompletionQueryId = async (queryId: number) => {
    if (completion === null) {
      return;
    }

    await apolloCore.mutate({
      mutation: UpdateWandCompletionQueryDocument,
      variables: {
        id: completion.response_id,
        query_id: queryId,
      },
      context: { session },
    });
  };

  return (
    <WandContext.Provider
      value={{
        isOpen,
        setIsOpen,
        mode,
        setMode,
        isRunning,
        setIsRunning,
        question,
        setQuestion,
        createQuery,
        editQuery,
        updateCompletionQueryId,
      }}
    >
      {children}
    </WandContext.Provider>
  );
}

export function useWand() {
  return useContext(WandContext);
}

async function createWandQuery(
  session: SessionWithUser,
  prompt: string,
  queryId: number | undefined
): Promise<WandCompletion | undefined> {
  try {
    const { data = null } = await apolloCore.mutate<
      CreateWandQueryMutation,
      CreateWandQueryMutationVariables
    >({
      mutation: CreateWandQueryDocument,
      variables: {
        prompt,
      },
      context: { session },
    });

    if (data === null) {
      throw new Error("No data returned");
    }

    const { response_id, query } = data.create_wand_query;

    await apolloCore.mutate<
      InsertWandCompletionMutation,
      InsertWandCompletionMutationVariables
    >({
      mutation: InsertWandCompletionDocument,
      variables: {
        id: response_id,
        question: prompt,
        answer: query,
        user_id: session.user.id,
        query_id: queryId,
      },
      context: { session },
    });

    return { response_id, query };
  } catch (error) {
    const description = error instanceof Error ? error.message : String(error);

    addToastNotification({
      level: "error",
      title: "Wand encountered an error",
      description,
    });
  }
}

async function editWandQuery(
  session: SessionWithUser,
  prompt: string,
  query: string
): Promise<WandCompletion | undefined> {
  try {
    const { data = null } = await apolloCore.mutate<
      EditWandQueryMutation,
      EditWandQueryMutationVariables
    >({
      mutation: EditWandQueryDocument,
      variables: {
        prompt,
        query,
      },
      context: { session },
    });

    if (data === null) {
      throw new Error("No data returned");
    }

    return data.edit_wand_query;
  } catch (error) {
    const description = error instanceof Error ? error.message : String(error);

    addToastNotification({
      level: "error",
      title: "Wand encountered an error",
      description,
    });
  }
}

export async function debugWandQuery(
  query: string,
  error: string,
  session: Session
) {
  try {
    const { data = null } = await apolloCore.mutate<
      DebugWandQueryMutation,
      DebugWandQueryMutationVariables
    >({
      mutation: DebugWandQueryDocument,
      variables: { query, error },
      context: { session },
    });

    if (data === null) {
      throw new Error("No data returned");
    }

    return data.debug_wand_query;
  } catch (error) {
    const description = error instanceof Error ? error.message : String(error);

    addToastNotification({
      level: "error",
      title: "Wand encountered an error",
      description,
    });
  }
}

gql`
  mutation CreateWandQuery($prompt: String!) {
    create_wand_query(prompt: $prompt) {
      response_id
      query
    }
  }
`;

gql`
  mutation EditWandQuery($prompt: String!, $query: String!) {
    edit_wand_query(prompt: $prompt, query: $query) {
      response_id
      query
    }
  }
`;

gql`
  mutation DebugWandQuery($error: String!, $query: String!) {
    debug_wand_query(error: $error, query: $query) {
      response_id
      query
    }
  }
`;

gql`
  mutation InsertWandCompletion(
    $id: uuid!
    $question: String!
    $answer: String!
    $user_id: Int!
    $query_id: Int
    $query_version: Int
  ) {
    insert_wand_completions_one(
      object: {
        id: $id
        question: $question
        answer: $answer
        user_id: $user_id
        query_id: $query_id
        query_version: $query_version
      }
    ) {
      id
    }
  }
`;

gql`
  mutation UpdateWandCompletionQuery(
    $id: uuid!
    $query_id: Int
    $query_version: Int
  ) {
    update_wand_completions(
      where: { id: { _eq: $id } }
      _set: { query_id: $query_id, query_version: $query_version }
    ) {
      affected_rows
    }
  }
`;
